import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Undo and redo over any piece of state, as a stack of whole snapshots.
 *
 * Two editing surfaces share this: the workflow canvas, which saves live and
 * has no Publish to walk away from, and the form builder, where a mis-drop
 * or a deleted card used to be gone for good until the next reload. Both
 * need the same two rules about what counts as ONE change:
 *
 *   Coalescing. Dragging a card fires a commit per animation frame, and
 *   typing a label fires one per keystroke. A person who presses undo means
 *   "put it back" / "take that word back", not one pixel or one letter, so
 *   consecutive commits carrying the same `coalesce` key replace the previous
 *   entry instead of stacking on it. A `coalesceMs` window turns a pause into
 *   a step boundary: type, stop for half a second, type again — two steps.
 *
 *   Barriers. When a change also touched something outside the snapshot (the
 *   canvas deletes a connections row with its node), no undo can honestly
 *   restore it. Such a change RESETS the history: you cannot step back past it.
 *
 * `cap` bounds the past so a long editing session cannot hold every snapshot
 * ever made; the oldest step falls off the far end.
 */
export interface CommitOptions {
  /** Changes sharing a key collapse into one entry while they keep arriving. */
  coalesce?: string;
  /** This change cannot be undone; everything before it becomes unreachable. */
  barrier?: boolean;
}

export interface HistoryOptions {
  /** The most steps kept behind the present. Unbounded when omitted. */
  cap?: number;
  /**
   * Same-key commits further apart than this many milliseconds are separate
   * steps. Omitted, a key coalesces for as long as it keeps arriving.
   */
  coalesceMs?: number;
  /** Clock, for tests. */
  now?: () => number;
}

/**
 * The actions, as ONE object whose identity never changes.
 *
 * This is load-bearing, not tidiness. A caller puts these in the dependency
 * list of the `useCallback` that loads its data; an object rebuilt every
 * render makes that callback change every render, which re-fires the effect
 * that calls it, which sets state, which renders again — a request storm.
 * That is why the booleans below are returned SEPARATELY from the actions.
 */
export interface HistoryActions<T> {
  /** Record a new state. */
  push: (next: T, opts?: CommitOptions) => void;
  /** The previous state, or null when there is nothing to go back to. */
  undo: () => T | null;
  /** The state undone from, or null. */
  redo: () => T | null;
  /** Start again from `state` — a fresh load, or an adoption. */
  reset: (state: T) => void;
  /**
   * End the current gesture without recording anything: the next commit
   * starts a new step even if it carries the same key.
   */
  settle: () => void;
}

export interface History<T> extends HistoryActions<T> {
  canUndo: boolean;
  canRedo: boolean;
  /** The stable half, for dependency lists. */
  actions: HistoryActions<T>;
}

export function useHistory<T>(options: HistoryOptions = {}): History<T> {
  const past = useRef<T[]>([]);
  const future = useRef<T[]>([]);
  const current = useRef<T | null>(null);
  /** The key the last entry was recorded under, and when, for coalescing. */
  const lastKey = useRef<string | null>(null);
  const lastAt = useRef(0);
  const opts = useRef(options);
  opts.current = options;
  const [, bump] = useState(0);
  const refresh = () => bump((n) => n + 1);
  const clock = () => (opts.current.now ?? Date.now)();

  const push = useCallback((next: T, o: CommitOptions = {}) => {
    if (o.barrier) {
      past.current = [];
      future.current = [];
      lastKey.current = null;
      current.current = next;
      refresh();
      return;
    }
    // A new change makes any undone ones unreachable — the ordinary rule.
    future.current = [];
    const at = clock();
    const window = opts.current.coalesceMs;
    const inWindow = window === undefined || at - lastAt.current <= window;
    const sameGesture = !!o.coalesce && o.coalesce === lastKey.current && inWindow;
    if (current.current !== null && !sameGesture) {
      past.current.push(current.current);
      const cap = opts.current.cap;
      if (cap !== undefined && past.current.length > cap) past.current.splice(0, past.current.length - cap);
    }
    lastKey.current = o.coalesce || null;
    lastAt.current = at;
    current.current = next;
    refresh();
  }, []);

  const undo = useCallback((): T | null => {
    if (!past.current.length) return null;
    const previous = past.current.pop() as T;
    if (current.current !== null) future.current.push(current.current);
    current.current = previous;
    // A gesture that has been undone must not absorb the next change.
    lastKey.current = null;
    refresh();
    return previous;
  }, []);

  const redo = useCallback((): T | null => {
    if (!future.current.length) return null;
    const next = future.current.pop() as T;
    if (current.current !== null) past.current.push(current.current);
    current.current = next;
    lastKey.current = null;
    refresh();
    return next;
  }, []);

  const reset = useCallback((state: T) => {
    past.current = [];
    future.current = [];
    lastKey.current = null;
    current.current = state;
    refresh();
  }, []);

  const settle = useCallback(() => { lastKey.current = null; }, []);

  // Memoised on nothing: every function above is already stable, so this
  // object is too, and a dependency list can hold it safely.
  const actions = useMemo(() => ({ push, undo, redo, reset, settle }), [push, undo, redo, reset, settle]);

  return {
    ...actions,
    actions,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
